/**
 * Pure helpers for the deprecate-release flow. No React, no side effects — these
 * are unit-tested in `deprecationPlan.test.js`.
 */

/**
 * Parse an entity PK into a normalized shape for display.
 *
 * PK formats:
 *   Event#hierarchy/name#version       → type, parent=hierarchy, name, version
 *   Resource#hierarchy/name#version    → type, parent=hierarchy, name, version
 *   CDT#name#version                   → type, parent='', name, version
 *
 * `parent` is the slash-separated subgroup path (everything before the final
 * segment); it is empty for CDTs and for hierarchy-less Event/Resource PKs.
 *
 * The key is `type#middle#version`. `type` never contains `#`; should the
 * version ever contain one, everything after the first two `#` is treated as
 * the version so it is not silently truncated.
 *
 * @param {string} pk
 * @returns {{ type: string, name: string, parent: string, version: string, pk: string }}
 */
export const parsePk = (pk = '') => {
  const parts = String(pk).split('#');
  const rawType = parts[0] || '';
  const middle = parts[1] || '';
  const version = parts.slice(2).join('#'); // tolerate '#' in version, if any
  const type = rawType.toLowerCase(); // event | resource | cdt
  const slash = middle.lastIndexOf('/');
  const name = slash === -1 ? middle : middle.slice(slash + 1);
  const parent = slash === -1 ? '' : middle.slice(0, slash);
  return { type, name, parent, version, pk: String(pk) };
};

/**
 * Strip the internal key prefix from a release identifier for display.
 *
 * The backend returns release identifiers as `Release#2.12.1`; users must only
 * ever see the version (`2.12.1`). Accepts a bare version too, so it is safe to
 * apply unconditionally.
 *
 * @param {string} release
 * @returns {string} the version portion, or '' for empty/invalid input
 */
export const formatReleaseVersion = (release = '') => {
  const str = String(release || '');
  const hash = str.lastIndexOf('#');
  return hash === -1 ? str : str.slice(hash + 1);
};

/**
 * Normalize the dry-run plan returned by the API into the shape the table
 * renders. Field names follow the event-api DeprecationPlanResponse contract
 * (camelCase via the backend's alias generator):
 *   {
 *     affectedEntities,
 *     entityActions:    [{ pk, action, demoteLatest, newReleaseField }],
 *     entityPromotions: [{ demotePk, promotePk }],
 *     releasePromoteTo, isResuming
 *   }
 * Note: the deprecate plan has no `warnings` field (that is delete-only).
 *
 * @param {object|null} plan
 */
export const normalizePlan = (plan) => {
  if (!plan || typeof plan !== 'object') {
    return { entities: [], affectedEntities: 0, releasePromoteTo: '', isResuming: false };
  }

  const actions = plan.entityActions || [];
  const promotions = plan.entityPromotions || [];

  // Map the entity losing Latest (demotePk) to the version gaining it, parsed
  // from promotePk, so we can show where the Latest flag repoints.
  const promoteToByPk = {};
  promotions.forEach((p) => {
    if (p.demotePk) promoteToByPk[p.demotePk] = parsePk(p.promotePk).version || '';
  });

  const entities = actions.map((a) => {
    const parsed = parsePk(a.pk);
    const action = (a.action || '').toLowerCase();
    const latestRepoint = Boolean(a.demoteLatest);
    const newVer = promoteToByPk[a.pk] || '';
    return {
      ...parsed,
      action: action || 'untag',
      newReleaseField: a.newReleaseField || '',
      // An entity is an "orphan risk" when it loses Latest but no other version
      // is promoted to take its place.
      orphan: latestRepoint && !newVer,
      latestRepoint,
      newVer,
    };
  });

  return {
    entities,
    affectedEntities: plan.affectedEntities ?? entities.length,
    // Strip the internal `Release#` prefix so the UI shows only the version.
    releasePromoteTo: formatReleaseVersion(plan.releasePromoteTo),
    isResuming: Boolean(plan.isResuming),
  };
};

/**
 * Drop any selected PKs that are not present in the given plan's entities.
 *
 * After a new dry-run the set of entities can change; stale selections would
 * otherwise be sent as `targetEntities` and rejected by the backend (400).
 *
 * @param {string[]} selectedPks
 * @param {{ entities?: Array<{ pk: string }> }|null} plan
 * @returns {string[]} the pruned selection (new array)
 */
export const pruneSelection = (selectedPks = [], plan) => {
  if (!Array.isArray(selectedPks) || selectedPks.length === 0) return [];
  const valid = new Set((plan?.entities || []).map((e) => e.pk));
  return selectedPks.filter((pk) => valid.has(pk));
};

/**
 * Whether the current selection differs from the selection the displayed plan
 * was generated for. When true, the preview no longer matches what would
 * execute, so a re-analysis is required.
 *
 * @param {string[]} selectedPks
 * @param {string[]} previewedPks
 */
export const hasSelectionDrifted = (selectedPks = [], previewedPks = []) => {
  if (selectedPks.length !== previewedPks.length) return true;
  const previewed = new Set(previewedPks);
  return selectedPks.some((pk) => !previewed.has(pk));
};

/**
 * Build the deprecate request body for a given selection.
 *   - empty selection  → Operation 1 (untag all, inactivate none)
 *   - non-empty        → Operation 2 (inactivate selected; sends targetEntities)
 *
 * @param {{ email: string, reason?: string, targets?: string[] }} args
 */
export const buildDeprecatePayload = ({ email, reason = '', targets = [] }) => {
  const payload = { deprecatedBy: (email || '').trim() };
  const trimmedReason = reason.trim();
  if (trimmedReason) payload.deprecatedDueTo = trimmedReason;
  if (Array.isArray(targets) && targets.length > 0) payload.targetEntities = targets;
  return payload;
};

/** RFC-lite email validity check used to gate the flow. */
export const isValidEmail = (email = '') => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());

/**
 * Count entities that are orphan risks (lose Latest with no replacement) in a
 * normalized plan. Shared by the deprecate and hard-delete flows.
 *
 * @param {Array<{ orphan?: boolean }>} entities
 * @returns {number}
 */
export const countOrphans = (entities = []) =>
  (Array.isArray(entities) ? entities : []).filter((e) => e && e.orphan).length;

/**
 * Whether the destructive action may be executed. Shared gate for both the
 * deprecate and hard-delete flows.
 *
 * Common rules: a valid authorizing email, the type-to-confirm value must match
 * the release version exactly, and no request in flight. When the plan has
 * orphan risks the user must have acknowledged the data loss.
 *
 * Deprecate adds one extra rule via `selectionDrifted`: if the current entity
 * selection no longer matches the previewed plan, a re-run is required first.
 * Hard-delete has no per-entity selection, so it passes `selectionDrifted:false`
 * (the default).
 *
 * @param {object} args
 * @param {boolean} args.emailValid
 * @param {string}  args.confirmVersion    current type-to-confirm input
 * @param {string}  args.releaseVersion    value the user must type
 * @param {boolean} args.isLoading         request in flight
 * @param {boolean} [args.orphanAckRequired=false]
 * @param {boolean} [args.orphanAck=false]
 * @param {boolean} [args.selectionDrifted=false]
 * @returns {boolean}
 */
export const canExecuteRelease = ({
  emailValid,
  confirmVersion,
  releaseVersion,
  isLoading,
  orphanAckRequired = false,
  orphanAck = false,
  selectionDrifted = false,
}) =>
  Boolean(emailValid) &&
  String(confirmVersion).trim() === releaseVersion &&
  !isLoading &&
  !selectionDrifted &&
  (!orphanAckRequired || Boolean(orphanAck));

/**
 * Whether the given release is the latest one.
 *
 * Prefers an explicit `latest`/`isLatest` flag from the API when present;
 * otherwise falls back to the highest semantic version among Active releases
 * (numeric-aware so 10.0.0 > 9.0.0). The latest release is the only one the
 * backend permits to be hard-deleted.
 *
 * @param {{ releaseVersion?: string, releaseStatus?: string, latest?: boolean, isLatest?: boolean }} release
 * @param {Array<object>} releases  the full release list
 * @returns {boolean}
 */
export const isLatestRelease = (release, releases = []) => {
  if (!release || !release.releaseVersion) return false;
  if (typeof release.latest === 'boolean') return release.latest;
  if (typeof release.isLatest === 'boolean') return release.isLatest;

  const latest = (Array.isArray(releases) ? releases : [])
    .filter((r) => r && (r.releaseStatus || 'Active') === 'Active')
    .map((r) => r.releaseVersion)
    .sort((a, b) => String(b).localeCompare(String(a), undefined, { numeric: true }))[0];

  return Boolean(latest) && latest === release.releaseVersion;
};

/**
 * Build the hard-delete request body. `reason` is required by the backend
 * (min 3 chars); the caller is responsible for validating before submit.
 *
 * @param {{ email: string, reason?: string, deleteEntities?: boolean, planId?: string }} args
 */
export const buildHardDeletePayload = ({
  email,
  reason = '',
  deleteEntities = false,
  planId = '',
}) => {
  const payload = {
    deletedBy: (email || '').trim(),
    reason: reason.trim(),
    deleteEntities: Boolean(deleteEntities),
  };
  if (planId) payload.planId = planId;
  return payload;
};

/**
 * Normalize a hard-delete dry-run plan into a view-friendly shape.
 *
 * Mirrors {@link normalizePlan} but for the delete contract: actions are
 * `delete`/`untag`, and the response carries cross-release `warnings`.
 *
 * @param {object|null} plan  raw HardDeletePlanResponse (camelCase)
 */
export const normalizeHardDeletePlan = (plan) => {
  if (!plan || typeof plan !== 'object') {
    return {
      entities: [],
      affectedEntities: 0,
      deleteMode: 'soft',
      warnings: [],
      s3Key: '',
      message: '',
      planId: '',
      actionCounts: { delete: 0, untag: 0, preservedShared: 0 },
    };
  }

  const actions = plan.entityActions || [];
  const promotions = plan.entityPromotions || [];

  const promoteToByPk = {};
  promotions.forEach((p) => {
    if (p.demotePk) promoteToByPk[p.demotePk] = parsePk(p.promotePk).version || '';
  });

  const entities = actions.map((a) => {
    const parsed = parsePk(a.pk);
    const action = (a.action || '').toLowerCase();
    const newVer = promoteToByPk[a.pk] || '';
    const latestRepoint = Boolean(promoteToByPk[a.pk]);
    // Whether this entity held the Latest tag. The delete contract may signal it
    // either via `demoteLatest` on the action or implicitly via a promotion
    // entry; honor both so orphan detection works regardless of which the
    // backend emits.
    const heldLatest = Boolean(a.demoteLatest) || latestRepoint;
    return {
      ...parsed,
      action: action || 'untag',
      newReleaseField: a.newReleaseField || '',
      latestRepoint,
      // Orphan risk: held Latest but no other version takes its place — Latest is
      // lost (entity exists only in this release), not moved to another release.
      orphan: heldLatest && !newVer,
      newVer,
    };
  });

  const warnings = (plan.warnings || []).map((w) => {
    const parsed = parsePk(w.pk || '');
    return {
      // Keep the raw pk as a stable React key only; presentation uses the
      // parsed fields so the internal key format is never shown to users.
      pk: w.pk || '',
      type: parsed.type,
      name: parsed.name,
      parent: parsed.parent,
      message: w.message || '',
      otherReleases: (Array.isArray(w.otherReleases) ? w.otherReleases : []).map(
        formatReleaseVersion
      ),
    };
  });

  const derivedCounts = {
    delete: entities.filter((entity) => entity.action === 'delete').length,
    untag: entities.filter((entity) => entity.action === 'untag').length,
    preservedShared: warnings.length,
  };
  const apiCounts = plan.actionCounts || {};
  const actionCounts = {
    delete: Number.isInteger(apiCounts.delete) ? apiCounts.delete : derivedCounts.delete,
    untag: Number.isInteger(apiCounts.untag) ? apiCounts.untag : derivedCounts.untag,
    preservedShared: Number.isInteger(apiCounts.preservedShared)
      ? apiCounts.preservedShared
      : derivedCounts.preservedShared,
  };

  return {
    entities,
    affectedEntities: plan.affectedEntities ?? entities.length,
    deleteMode: plan.deleteMode || 'soft',
    warnings,
    s3Key: plan.s3Key || '',
    message: plan.message || '',
    planId: plan.planId || '',
    actionCounts,
  };
};

// Add (checked) or remove a batch of PKs from the selection; idempotent, returns a new array.
export const mergeSelection = (selectedPks = [], pks = [], checked) => {
  const set = new Set(selectedPks);
  (Array.isArray(pks) ? pks : []).forEach((pk) => (checked ? set.add(pk) : set.delete(pk)));
  return Array.from(set);
};

/**
 * Filter normalized entities by the active type tab, a free-text query
 * (matches name or hierarchy path), and an orphan-only toggle.
 *
 * @param {Array} entities
 * @param {{ activeTab?: string, search?: string, showOrphansOnly?: boolean }} opts
 */
export const filterEntities = (
  entities = [],
  { activeTab = 'all', search = '', showOrphansOnly = false } = {}
) => {
  const q = search.trim().toLowerCase();
  return entities.filter((entity) => {
    const tabMatch = activeTab === 'all' || entity.type === activeTab;
    const searchMatch =
      !q ||
      entity.name.toLowerCase().includes(q) ||
      (entity.parent || '').toLowerCase().includes(q);
    const orphanMatch = showOrphansOnly ? entity.orphan : true;
    return tabMatch && searchMatch && orphanMatch;
  });
};
